import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import "./DisclosureConsent.css";

const disclosureStatuses = [
  {
    value: "REQUESTED",
    label: "Requested",
  },
  {
    value: "APPROVED",
    label: "Approved",
  },
  {
    value: "DECLINED",
    label: "Declined",
  },
  {
    value: "EXPIRED",
    label: "Expired",
  },
];

const consentDecisions = [
  {
    value: "GRANTED",
    label: "Granted",
  },
  {
    value: "DECLINED",
    label: "Declined",
  },
  {
    value: "WITHDRAWN",
    label: "Withdrawn",
  },
];

const DisclosureConsent = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("disclosure");

  const [disclosureData, setDisclosureData] = useState({
    project: "",
    professional: "",
    requested_by: "",
    requested_fields: [],
    purpose: "",
    status: "REQUESTED",
    expires_at: "",
  });

  const [consentData, setConsentData] = useState({
    disclosure_request: "",
    professional: "",
    decision: "GRANTED",
    fields: [],
    withdrawn_at: "",
  });

  const [fieldInput, setFieldInput] = useState("");
  const [consentFieldInput, setConsentFieldInput] =
    useState("");

  const handleDisclosureChange = (e) => {
    const { name, value } = e.target;

    setDisclosureData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleConsentChange = (e) => {
    const { name, value } = e.target;

    setConsentData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =========================
     Disclosure Requested Fields
  ========================= */

  const addRequestedField = () => {
    const field = fieldInput.trim();

    if (!field) return;

    if (disclosureData.requested_fields.includes(field)) {
      setFieldInput("");
      return;
    }

    setDisclosureData((prev) => ({
      ...prev,
      requested_fields: [
        ...prev.requested_fields,
        field,
      ],
    }));

    setFieldInput("");
  };

  const removeRequestedField = (fieldToRemove) => {
    setDisclosureData((prev) => ({
      ...prev,
      requested_fields:
        prev.requested_fields.filter(
          (field) => field !== fieldToRemove
        ),
    }));
  };

  const handleRequestedFieldKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addRequestedField();
    }
  };

  /* =========================
     Consent Fields
  ========================= */

  const addConsentField = () => {
    const field = consentFieldInput.trim();

    if (!field) return;

    if (consentData.fields.includes(field)) {
      setConsentFieldInput("");
      return;
    }

    setConsentData((prev) => ({
      ...prev,
      fields: [...prev.fields, field],
    }));

    setConsentFieldInput("");
  };

  const removeConsentField = (fieldToRemove) => {
    setConsentData((prev) => ({
      ...prev,
      fields: prev.fields.filter(
        (field) => field !== fieldToRemove
      ),
    }));
  };

  const handleConsentFieldKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addConsentField();
    }
  };

  /* =========================
     Submit
  ========================= */

  const handleSubmit = (e) => {
    e.preventDefault();

    if (activeTab === "disclosure") {
      if (disclosureData.requested_fields.length === 0) {
        alert(
          "Please add at least one requested profile field."
        );
        return;
      }

      if (!disclosureData.purpose.trim()) {
        alert("Please enter the purpose of the request.");
        return;
      }

      console.log(
        "Disclosure Request:",
        disclosureData
      );

      alert(
        "Disclosure request saved successfully."
      );
    } else {
      if (consentData.fields.length === 0) {
        alert(
          "Please select at least one consented field."
        );
        return;
      }

      if (
        consentData.decision === "WITHDRAWN" &&
        !consentData.withdrawn_at
      ) {
        alert(
          "Please enter the withdrawal date and time."
        );
        return;
      }

      console.log(
        "Candidate Consent:",
        consentData
      );

      alert(
        "Candidate consent saved successfully."
      );
    }
  };

  const handleCancel = () => {
    navigate("/admin/disclosure-consent");
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="disclosure-consent-content">
          {/* Page Header */}
          <div className="disclosure-consent-page-header">
            <div>
              <h1>Disclosure & Consent</h1>

              <p>
                Manage field-level disclosure requests and
                candidate consent decisions.
              </p>
            </div>

            <button
              type="button"
              className="disclosure-consent-back-btn"
              onClick={handleCancel}
            >
              Back
            </button>
          </div>

          {/* Tabs */}
          <div className="disclosure-consent-tabs">
            <button
              type="button"
              className={`disclosure-consent-tab ${
                activeTab === "disclosure"
                  ? "active"
                  : ""
              }`}
              onClick={() =>
                setActiveTab("disclosure")
              }
            >
              Disclosure Request
            </button>

            <button
              type="button"
              className={`disclosure-consent-tab ${
                activeTab === "consent"
                  ? "active"
                  : ""
              }`}
              onClick={() =>
                setActiveTab("consent")
              }
            >
              Candidate Consent
            </button>
          </div>

          {/* Form */}
          <form
            className="disclosure-consent-card"
            onSubmit={handleSubmit}
          >
            {/* =========================
                DISCLOSURE REQUEST TAB
            ========================= */}

            {activeTab === "disclosure" && (
              <>
                <div className="disclosure-consent-card-header">
                  <div>
                    <h2>Disclosure Request</h2>

                    <p>
                      Create a field-level request to access
                      specific candidate information.
                    </p>
                  </div>

                  <span className="disclosure-consent-status-badge requested">
                    Requested
                  </span>
                </div>

                <div className="disclosure-consent-card-body">
                  {/* Request Information */}
                  <div className="disclosure-consent-section">
                    <div className="disclosure-consent-section-title">
                      <h3>Request Information</h3>

                      <p>
                        Select the project, professional and
                        user requesting the disclosure.
                      </p>
                    </div>

                    <div className="disclosure-consent-grid">
                      <div className="disclosure-consent-field">
                        <label>
                          Project <span>*</span>
                        </label>

                        <select
                          name="project"
                          value={disclosureData.project}
                          onChange={
                            handleDisclosureChange
                          }
                          required
                        >
                          <option value="">
                            Select project
                          </option>

                          <option value="1">
                            Project Alpha
                          </option>

                          <option value="2">
                            Project Beta
                          </option>

                          <option value="3">
                            Project Gamma
                          </option>
                        </select>
                      </div>

                      <div className="disclosure-consent-field">
                        <label>
                          Professional <span>*</span>
                        </label>

                        <select
                          name="professional"
                          value={
                            disclosureData.professional
                          }
                          onChange={
                            handleDisclosureChange
                          }
                          required
                        >
                          <option value="">
                            Select professional
                          </option>

                          <option value="1">
                            Professional 001
                          </option>

                          <option value="2">
                            Professional 002
                          </option>

                          <option value="3">
                            Professional 003
                          </option>
                        </select>
                      </div>

                      <div className="disclosure-consent-field">
                        <label>
                          Requested By <span>*</span>
                        </label>

                        <select
                          name="requested_by"
                          value={
                            disclosureData.requested_by
                          }
                          onChange={
                            handleDisclosureChange
                          }
                          required
                        >
                          <option value="">
                            Select user
                          </option>

                          <option value="1">
                            Project Manager
                          </option>

                          <option value="2">
                            HR Manager
                          </option>

                          <option value="3">
                            Organisation Admin
                          </option>
                        </select>
                      </div>

                      <div className="disclosure-consent-field">
                        <label>Status</label>

                        <select
                          name="status"
                          value={disclosureData.status}
                          onChange={
                            handleDisclosureChange
                          }
                        >
                          {disclosureStatuses.map(
                            (item) => (
                              <option
                                key={item.value}
                                value={item.value}
                              >
                                {item.label}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Requested Fields */}
                  <div className="disclosure-consent-section">
                    <div className="disclosure-consent-section-title">
                      <h3>Requested Fields</h3>

                      <p>
                        Specify exactly which candidate
                        profile fields are required. Blanket
                        disclosure requests should not be used.
                      </p>
                    </div>

                    <div className="disclosure-consent-tag-input-row">
                      <input
                        type="text"
                        value={fieldInput}
                        onChange={(e) =>
                          setFieldInput(
                            e.target.value
                          )
                        }
                        onKeyDown={
                          handleRequestedFieldKeyDown
                        }
                        placeholder="e.g. email, phone, experience"
                      />

                      <button
                        type="button"
                        className="disclosure-consent-add-btn"
                        onClick={
                          addRequestedField
                        }
                      >
                        Add Field
                      </button>
                    </div>

                    {disclosureData.requested_fields
                      .length > 0 && (
                      <div className="disclosure-consent-tag-list">
                        {disclosureData.requested_fields.map(
                          (field) => (
                            <div
                              key={field}
                              className="disclosure-consent-tag"
                            >
                              <span>{field}</span>

                              <button
                                type="button"
                                onClick={() =>
                                  removeRequestedField(
                                    field
                                  )
                                }
                              >
                                ×
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    )}

                    {disclosureData.requested_fields
                      .length === 0 && (
                      <div className="disclosure-consent-empty">
                        No profile fields added yet.
                      </div>
                    )}
                  </div>

                  {/* Purpose */}
                  <div className="disclosure-consent-section">
                    <div className="disclosure-consent-section-title">
                      <h3>Purpose & Expiry</h3>

                      <p>
                        Every disclosure request must have a
                        defined business purpose and optional
                        expiry.
                      </p>
                    </div>

                    <div className="disclosure-consent-grid">
                      <div className="disclosure-consent-field disclosure-consent-full-field">
                        <label>
                          Purpose <span>*</span>
                        </label>

                        <textarea
                          name="purpose"
                          value={
                            disclosureData.purpose
                          }
                          onChange={
                            handleDisclosureChange
                          }
                          placeholder="Explain why these specific candidate fields are required..."
                          rows="4"
                          required
                        />
                      </div>

                      <div className="disclosure-consent-field">
                        <label>Expires At</label>

                        <input
                          type="datetime-local"
                          name="expires_at"
                          value={
                            disclosureData.expires_at
                          }
                          onChange={
                            handleDisclosureChange
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {/* Information */}
                  <div className="disclosure-consent-info-box">
                    <strong>
                      Field-Level Disclosure
                    </strong>

                    <p>
                      Disclosure requests must be specific
                      and purpose-bound. Only the requested
                      profile fields should become eligible
                      for candidate consent.
                    </p>
                  </div>

                  {/* Summary */}
                  <div className="disclosure-consent-summary">
                    <div className="disclosure-consent-summary-item">
                      <span>Project</span>

                      <strong>
                        {disclosureData.project
                          ? `Project ${disclosureData.project}`
                          : "Not selected"}
                      </strong>
                    </div>

                    <div className="disclosure-consent-summary-item">
                      <span>Professional</span>

                      <strong>
                        {disclosureData.professional
                          ? `Professional ${disclosureData.professional}`
                          : "Not selected"}
                      </strong>
                    </div>

                    <div className="disclosure-consent-summary-item">
                      <span>Requested Fields</span>

                      <strong>
                        {
                          disclosureData
                            .requested_fields
                            .length
                        }
                      </strong>
                    </div>

                    <div className="disclosure-consent-summary-item">
                      <span>Status</span>

                      <strong>
                        {
                          disclosureStatuses.find(
                            (item) =>
                              item.value ===
                              disclosureData.status
                          )?.label
                        }
                      </strong>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* =========================
                CANDIDATE CONSENT TAB
            ========================= */}

            {activeTab === "consent" && (
              <>
                <div className="disclosure-consent-card-header">
                  <div>
                    <h2>Candidate Consent</h2>

                    <p>
                      Record the candidate's decision on a
                      disclosure request.
                    </p>
                  </div>

                  <span className="disclosure-consent-status-badge granted">
                    Candidate Decision
                  </span>
                </div>

                <div className="disclosure-consent-card-body">
                  {/* Consent Information */}
                  <div className="disclosure-consent-section">
                    <div className="disclosure-consent-section-title">
                      <h3>Consent Information</h3>

                      <p>
                        Link the consent to an existing
                        disclosure request and professional.
                      </p>
                    </div>

                    <div className="disclosure-consent-grid">
                      <div className="disclosure-consent-field">
                        <label>
                          Disclosure Request <span>*</span>
                        </label>

                        <select
                          name="disclosure_request"
                          value={
                            consentData.disclosure_request
                          }
                          onChange={
                            handleConsentChange
                          }
                          required
                        >
                          <option value="">
                            Select disclosure request
                          </option>

                          <option value="1">
                            DR-0001 — Project Alpha
                          </option>

                          <option value="2">
                            DR-0002 — Project Beta
                          </option>

                          <option value="3">
                            DR-0003 — Project Gamma
                          </option>
                        </select>
                      </div>

                      <div className="disclosure-consent-field">
                        <label>
                          Professional <span>*</span>
                        </label>

                        <select
                          name="professional"
                          value={
                            consentData.professional
                          }
                          onChange={
                            handleConsentChange
                          }
                          required
                        >
                          <option value="">
                            Select professional
                          </option>

                          <option value="1">
                            Professional 001
                          </option>

                          <option value="2">
                            Professional 002
                          </option>

                          <option value="3">
                            Professional 003
                          </option>
                        </select>
                      </div>

                      <div className="disclosure-consent-field">
                        <label>
                          Decision <span>*</span>
                        </label>

                        <select
                          name="decision"
                          value={consentData.decision}
                          onChange={
                            handleConsentChange
                          }
                          required
                        >
                          {consentDecisions.map(
                            (item) => (
                              <option
                                key={item.value}
                                value={item.value}
                              >
                                {item.label}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Consented Fields */}
                  <div className="disclosure-consent-section">
                    <div className="disclosure-consent-section-title">
                      <h3>Consented Fields</h3>

                      <p>
                        Select only the fields the candidate
                        has actually agreed to disclose.
                      </p>
                    </div>

                    <div className="disclosure-consent-tag-input-row">
                      <input
                        type="text"
                        value={consentFieldInput}
                        onChange={(e) =>
                          setConsentFieldInput(
                            e.target.value
                          )
                        }
                        onKeyDown={
                          handleConsentFieldKeyDown
                        }
                        placeholder="e.g. email, phone"
                      />

                      <button
                        type="button"
                        className="disclosure-consent-add-btn"
                        onClick={addConsentField}
                      >
                        Add Field
                      </button>
                    </div>

                    {consentData.fields.length > 0 && (
                      <div className="disclosure-consent-tag-list">
                        {consentData.fields.map(
                          (field) => (
                            <div
                              key={field}
                              className="disclosure-consent-tag"
                            >
                              <span>{field}</span>

                              <button
                                type="button"
                                onClick={() =>
                                  removeConsentField(
                                    field
                                  )
                                }
                              >
                                ×
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    )}

                    {consentData.fields.length === 0 && (
                      <div className="disclosure-consent-empty">
                        No consented fields added yet.
                      </div>
                    )}
                  </div>

                  {/* Withdrawal */}
                  <div className="disclosure-consent-section">
                    <div className="disclosure-consent-section-title">
                      <h3>Consent Lifecycle</h3>

                      <p>
                        Withdrawal date is required when
                        previously granted consent is withdrawn.
                      </p>
                    </div>

                    <div className="disclosure-consent-grid">
                      <div className="disclosure-consent-field">
                        <label>Withdrawn At</label>

                        <input
                          type="datetime-local"
                          name="withdrawn_at"
                          value={
                            consentData.withdrawn_at
                          }
                          onChange={
                            handleConsentChange
                          }
                          disabled={
                            consentData.decision !==
                            "WITHDRAWN"
                          }
                        />

                        <small>
                          This field becomes available when
                          the decision is set to Withdrawn.
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Candidate Decision Box */}
                  <div className="disclosure-consent-decision-box">
                    <div className="disclosure-consent-decision-icon">
                      ✓
                    </div>

                    <div>
                      <strong>
                        Candidate-Controlled Consent
                      </strong>

                      <p>
                        Consent represents the candidate's own
                        decision. It should never be entered or
                        approved on the candidate's behalf.
                      </p>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="disclosure-consent-summary">
                    <div className="disclosure-consent-summary-item">
                      <span>Disclosure Request</span>

                      <strong>
                        {consentData.disclosure_request
                          ? `DR-${consentData.disclosure_request}`
                          : "Not selected"}
                      </strong>
                    </div>

                    <div className="disclosure-consent-summary-item">
                      <span>Professional</span>

                      <strong>
                        {consentData.professional
                          ? `Professional ${consentData.professional}`
                          : "Not selected"}
                      </strong>
                    </div>

                    <div className="disclosure-consent-summary-item">
                      <span>Consented Fields</span>

                      <strong>
                        {consentData.fields.length}
                      </strong>
                    </div>

                    <div className="disclosure-consent-summary-item">
                      <span>Decision</span>

                      <strong>
                        {
                          consentDecisions.find(
                            (item) =>
                              item.value ===
                              consentData.decision
                          )?.label
                        }
                      </strong>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Actions */}
            <div className="disclosure-consent-actions">
              <button
                type="button"
                className="disclosure-consent-cancel-btn"
                onClick={handleCancel}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="disclosure-consent-save-btn"
              >
                {activeTab === "disclosure"
                  ? "Save Disclosure Request"
                  : "Save Candidate Consent"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DisclosureConsent;